'use client';

import React from 'react';
import classnames from 'classnames';

import Typography, { type TypographyColor } from '../../atoms/Typography/Typography';
import Box from '../../atoms/Box/Box';
import Iconography, { IconographyProps } from '../Iconography/Iconography';

import styles from './Spec.module.scss';

type Variant = 'default' | 'sapphire' | 'white' | 'gray';
type Style = 'italic' | 'default';
type Alignment = 'left' | 'center';
type Size = 'extraSmall' | 'small' | 'medium' | 'large';
type Direction = 'ltr' | 'rtl';
type Type = 'serif' | 'sans';
type Theme = 'dark' | 'light';

interface CarInfo {
  [key: string]: unknown;
}

interface ColorConfig {
  eyebrow: TypographyColor;
  title: TypographyColor;
  description: TypographyColor;
}

interface FontSizeConfig {
  eyebrow: string;
  title: string;
  description: string;
}

const color: Record<Variant, ColorConfig> = {
  default: {
    eyebrow: 'primary-1',
    title: 'white',
    description: 'gray-5',
  },
  sapphire: {
    eyebrow: 'product-sapphire-4',
    title: 'white',
    description: 'white',
  },
  white: {
    eyebrow: 'white',
    title: 'white',
    description: 'white',
  },
  gray: {
    eyebrow: 'gray-5',
    title: 'white',
    description: 'gray-5',
  },
};

const fontSize: Record<Type, Record<Size, FontSizeConfig>> = {
  sans: {
    extraSmall: {
      eyebrow: 'small',
      title: 'small',
      description: 'extraSmall',
    },
    small: {
      eyebrow: 'small',
      title: 'small',
      description: 'small',
    },
    medium: {
      eyebrow: 'medium',
      title: 'medium',
      description: 'small',
    },
    large: {
      eyebrow: 'medium',
      title: 'large',
      description: 'small',
    },
  },
  serif: {
    extraSmall: {
      eyebrow: 'extraSmall',
      title: 'extraSmall',
      description: 'extraSmall',
    },
    small: {
      eyebrow: 'extraSmall',
      title: 'small',
      description: 'small',
    },
    medium: {
      eyebrow: 'small',
      title: 'medium',
      description: 'small',
    },
    large: {
      eyebrow: 'small',
      title: 'large',
      description: 'small',
    },
  },
};

export interface SpecProps {
  variant?: Variant;
  title?: string;
  eyebrow?: string;
  description?: string;
  disclaimer?: string;
  style?: Style;
  alignment?: Alignment;
  size?: Size;
  className?: string;
  carInfo?: CarInfo;
  direction?: Direction;
  type?: Type;
  icon?: Pick<IconographyProps, 'name' | 'category' | 'color'>;
  handleIconClick?: () => void;
  theme?: Theme;
  showSkeleton?: boolean;
  uiId?: string;
}

// Simple placeholder for price replacement utility
const replaceTextWithPrices = ({ text }: { text: string; data?: CarInfo }) => {
  // TODO: Implement proper price replacement when utils are available
  return text;
};

const Spec: React.FC<SpecProps> = ({
  variant = 'default',
  title,
  eyebrow,
  description,
  disclaimer = '',
  style = 'default',
  alignment = 'left',
  size = 'medium',
  className = '',
  carInfo,
  direction = 'ltr',
  type = 'sans',
  icon,
  handleIconClick,
  theme = 'dark',
  showSkeleton = false,
  uiId,
}) => {
  return (
    <Box
      className={classnames(
        styles.specContainer,
        {
          [styles.centerAligned]: showSkeleton && alignment === 'center',
        },
        className
      )}
    >
      {eyebrow && (
        <Typography
          variant={type === 'serif' ? 'body' : 'label'}
          size={fontSize?.[type]?.[size]?.eyebrow as 'small' | 'medium' | 'large' | undefined}
          align={alignment}
          color={color[variant].eyebrow}
          className={styles.eyebrow}
          direction={direction}
          theme={theme}
          showSkeleton={showSkeleton}
          skeletonWidth={{ xs: 40, md: 60, xl: 60 }}
        >
          {eyebrow}
        </Typography>
      )}
      <Box className={classnames(styles.titleContainer, styles[alignment])}>
        {title && (
          <Typography
            variant={
              style === 'italic' || type === 'serif'
                ? 'headline'
                : size === 'extraSmall'
                  ? 'body'
                  : 'subtitle'
            }
            size={
              fontSize?.[type]?.[size]?.title as
                | 'extraSmall'
                | 'small'
                | 'medium'
                | 'large'
                | undefined
            }
            align={alignment}
            fontStyle={style}
            color={color[variant].title}
            direction={direction}
            theme={theme}
            showSkeleton={showSkeleton}
            skeletonWidth={{ xs: 50, md: 70, xl: 70 }}
          >
            {`${replaceTextWithPrices({ text: title, data: carInfo })}${disclaimer && '<sup>' + disclaimer + '</sup>'}`}
          </Typography>
        )}
        {icon && (
          <Iconography
            handleClick={() => {
              handleIconClick && handleIconClick();
            }}
            name={icon?.name}
            category={icon?.category}
            color={icon?.color ? icon?.color : 'white'}
            data-uiid={uiId}
          />
        )}
      </Box>
      {description && (
        <Typography
          className={styles.description}
          variant="body"
          size={fontSize?.[type]?.[size]?.description as 'small' | 'medium' | 'large' | undefined}
          align={alignment}
          color={color[variant].description}
          direction={direction}
          theme={theme}
          showSkeleton={showSkeleton}
          skeletonWidth={{ xs: 60, md: 80, xl: 80 }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
};

Spec.displayName = 'Spec';

export default Spec;
